import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
    distinctUntilChanged,
    EMPTY,
    map,
    Observable,
    of,
    Subject,
    takeUntil,
} from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
    AppointmentData,
    AppointmentInvoiceView,
    AppointmentListResponse,
    AppointmentLocationSnapshot,
    AppointmentStatus,
    AppointmentType,
    AuctionCatalogsService,
} from '@viewbid/ts-angular-client';
import { fmtDate, fmtTime, VbSnackbarService } from '@viewbid/ui';
import { ProductService } from '@/shared/services/product.service';
import { DeviceCheckService } from 'src/app/viewbid/shared/services/device-check.service';
import {
    navigateProfilePaging,
    ProfilePaging,
    resolveProfilePaging,
} from 'src/app/viewbid/shared/utils/profile-paging.util';
import { FeatureFlagUtil } from 'src/app/core/utils/feature-flag.util';
import { FeatureFlag } from 'src/app/core/constants/feature-flag.enum';
import {
    VbAppointmentInvoiceEvent,
    VbAppointmentRowStatus,
    VbAppointmentTableRow,
    VbAppointmentType,
    VbLegendItem,
} from '@viewbid/ui';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'vb-user-appointments',
    templateUrl: './user-appointments.component.html',
    styleUrls: ['./user-appointments.component.scss'],
})
export class UserAppointmentsComponent implements OnInit, OnDestroy {
    constructor(
        private route: ActivatedRoute,
        private router: Router,
        public productService: ProductService,
        private auctionCatalogsService: AuctionCatalogsService,
        private deviceCheckService: DeviceCheckService,
        private snackbarService: VbSnackbarService,
        private featureFlagUtil: FeatureFlagUtil,
        private translate: TranslateService,
    ) {}

    appointments: AppointmentData[] = [];
    /** Presentation rows for <vb-appointment-table>, derived from appointments. */
    public rows: VbAppointmentTableRow[] = [];
    /** `${rowId}:${invoiceNo}` → invoicesId, so invoiceOpen can navigate to
     *  the real invoice record (the table only carries the display number). */
    private invoiceIdByRef: Map<string, string> = new Map();
    public paginate: any = {};
    public isLoading: boolean = true;
    public pageNo: number = 1;
    public pageSize: number = 10;
    private readonly defaultPageSize: number = this.pageSize;
    public readonly pageSizeOptions: ReadonlyArray<number> = [10, 25, 50];
    public totalCount: number = 0;
    public unableToLoad: boolean = false;
    public isMobile: boolean = false;
    private mobile$ = new Subject<void>();
    private destroy$ = new Subject<void>();

    public readonly legendItems: VbLegendItem[] = [
        { tone: 'positive', label: 'Confirmed' },
        { tone: 'danger', label: 'Cancelled' },
    ];

    ngOnInit(): void {
        // Belt-and-suspenders with the sidebar's tab gating: block direct
        // navigation to this route while the feature is off (dev instance
        // only for now — see environment.dev.ts).
        if (
            !this.featureFlagUtil.isFeatureEnabled(FeatureFlag.MyAppointments)
        ) {
            this.router.navigate(['../profile'], { relativeTo: this.route });
            return;
        }

        this.route.queryParams
            .pipe(
                map((params: Params): ProfilePaging => {
                    navigateProfilePaging(
                        this.router,
                        this.route,
                        params,
                        this.defaultPageSize,
                        true,
                    );
                    return resolveProfilePaging(params, this.defaultPageSize);
                }),
                distinctUntilChanged(
                    (previous: ProfilePaging, current: ProfilePaging) =>
                        previous.page === current.page &&
                        previous.pageSize === current.pageSize,
                ),
                takeUntil(this.destroy$),
            )
            .subscribe((paging: ProfilePaging) => {
                this.pageNo = paging.page;
                this.pageSize = paging.pageSize;
                this.loadAppointments();
            });

        this.deviceCheckService.isMobile$
            .pipe(takeUntil(this.mobile$))
            .subscribe((isMobile: boolean) => {
                this.isMobile = isMobile;
            });
    }

    private loadAppointments(): void {
        const paging: ProfilePaging = {
            page: this.pageNo,
            pageSize: this.pageSize,
        };
        this.isLoading = true;
        this.auctionCatalogsService
            .listMyAppointments(
                undefined,
                undefined,
                undefined,
                paging.page,
                paging.pageSize,
            )
            .pipe(
                switchMap((response: AppointmentListResponse) => {
                    if (
                        paging.page !== this.pageNo ||
                        paging.pageSize !== this.pageSize
                    ) {
                        return EMPTY;
                    }
                    const correctedPaging: ProfilePaging = resolveProfilePaging(
                        paging,
                        this.defaultPageSize,
                        response.totalCount ?? 0,
                    );
                    if (correctedPaging.page !== paging.page) {
                        navigateProfilePaging(
                            this.router,
                            this.route,
                            correctedPaging,
                            this.defaultPageSize,
                            true,
                        );
                        return EMPTY;
                    }
                    return this.hydrateRows(response);
                }),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: (rows: VbAppointmentTableRow[]) => {
                    this.rows = rows;
                    this.isLoading = false;
                },
                error: () => {
                    if (
                        paging.page !== this.pageNo ||
                        paging.pageSize !== this.pageSize
                    ) {
                        return;
                    }
                    this.isLoading = false;
                    this.unableToLoad = true;
                },
            });
    }

    /** Sets pagination state from the list response, then maps onto
     *  vb-appointment-table rows — location, appointment type, and seller
     *  all come straight off the appointment. */
    private hydrateRows(
        response: AppointmentListResponse,
    ): Observable<VbAppointmentTableRow[]> {
        this.appointments = response.data ?? [];

        if (this.appointments.length === 0) {
            this.unableToLoad = true;
            this.totalCount = 0;
            this.paginate = this.productService.getPager(
                0,
                0,
                this.pageSize,
                false,
            );
            this.paginate.currentPage = 0;
            return of([]);
        }

        this.totalCount = response.totalCount ?? 0;
        this.paginate = this.productService.getPager(
            response.totalCount ?? 0,
            this.pageNo,
            this.pageSize,
            false,
        );

        return of(this.buildRows());
    }

    private buildRows(): VbAppointmentTableRow[] {
        this.invoiceIdByRef.clear();
        return this.appointments.map((appointment: AppointmentData) => {
            const seller: string = appointment.seller?.name ?? 'Viewbid';
            const type: VbAppointmentType = this.toVbAppointmentType(
                appointment.appointmentType ?? undefined,
            );

            appointment.invoices.forEach((invoice: AppointmentInvoiceView) => {
                this.invoiceIdByRef.set(
                    `${appointment.id}:${invoice.invoiceNumber}`,
                    invoice.invoicesId,
                );
            });

            return {
                id: appointment.id,
                date: fmtDate(appointment.pickupTime),
                time:
                    type === 'shipping' ? '—' : fmtTime(appointment.pickupTime),
                seller,
                locationName: appointment.location?.name ?? '',
                addressLines: this.addressLines(
                    appointment.location ?? undefined,
                ),
                type,
                status: this.toVbAppointmentStatus(appointment.status),
                invoices: appointment.invoices.map(
                    (invoice: AppointmentInvoiceView) => ({
                        invoiceNo: String(invoice.invoiceNumber),
                    }),
                ),
                cancellable: appointment.status === 'Upcoming',
            };
        });
    }

    private addressLines(location?: AppointmentLocationSnapshot): string[] {
        if (!location) return [];
        const cityLine: string = [
            location.city,
            location.province,
            location.postalCode,
        ]
            .filter(Boolean)
            .join(', ');
        return [location.address1, location.address2, cityLine].filter(
            (line: string | null | undefined): line is string => !!line,
        );
    }

    private toVbAppointmentType(type?: AppointmentType): VbAppointmentType {
        switch (type) {
            case 'Smartbox':
                return 'smartbox';
            case 'Automated':
                return 'shipping';
            default:
                return 'regular-pickup';
        }
    }

    private toVbAppointmentStatus(
        status: AppointmentStatus,
    ): VbAppointmentRowStatus {
        return status === 'Cancelled' ? 'cancelled' : 'confirmed';
    }

    setPage(page: number): void {
        navigateProfilePaging(
            this.router,
            this.route,
            { page, pageSize: this.pageSize },
            this.defaultPageSize,
        );
    }

    setPageSize(pageSize: number): void {
        navigateProfilePaging(
            this.router,
            this.route,
            { page: 1, pageSize },
            this.defaultPageSize,
        );
    }

    onCancelAppointment(row: VbAppointmentTableRow): void {
        this.auctionCatalogsService.cancelPublicAppointment(row.id).subscribe({
            next: () => {
                this.snackbarService.success(
                    this.translate.instant(
                        'Invoices.cancelAppointmentSuccess',
                        {
                            date: row.date,
                            time: row.time,
                        },
                    ),
                );
                this.loadAppointments();
            },
            error: () => {
                this.snackbarService.error(
                    this.translate.instant('Invoices.cancelAppointmentError'),
                );
            },
        });
    }

    onInvoiceOpen(event: VbAppointmentInvoiceEvent): void {
        const invoicesId: string | undefined = this.invoiceIdByRef.get(
            `${event.row.id}:${event.invoice.invoiceNo}`,
        );
        if (!invoicesId) return;
        this.router.navigate(['../view-invoice', invoicesId], {
            relativeTo: this.route,
        });
    }

    ngOnDestroy(): void {
        this.mobile$.next();
        this.mobile$.complete();
        this.destroy$.next();
        this.destroy$.complete();
    }
}
